import { useEffect, useState, useCallback, Fragment } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentContext";
import { hasPerm } from "@/lib/perms";
import { Search, History, Save, X, Download, RotateCcw, Lock } from "lucide-react";

const inputDark = "mt-1 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-slate-500";
const LONG_TYPES = ["description", "message"];

function StatCard({ label, value, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900 p-4">
      <div className="text-xs text-slate-400">{label}</div>
      <div className="mt-1 font-display text-2xl font-bold text-white">{value}</div>
    </div>
  );
}

function Badge({ status }) {
  const { t } = useContent();
  const map = { modified: "bg-emerald-500/15 text-emerald-300", default: "bg-slate-700/40 text-slate-400" };
  return <span className={`rounded-full px-2 py-0.5 text-[11px] ${map[status] || map.default}`}>{status === "modified" ? t("admin.cms.modified", "Modifié") : t("admin.cms.default", "Défaut")}</span>;
}

export default function AdminContent() {
  const { t } = useContent();
  const { category } = useParams();
  const nav = useNavigate();
  const base = useLocation().pathname.startsWith("/agent") ? "/agent/content" : "/admin/content";
  const { user } = useAuth();
  const canEdit = hasPerm(user, "EDIT_SITE_CONTENT");
  const canPublish = hasPerm(user, "PUBLISH_SITE_CONTENT");
  const canEmail = hasPerm(user, "EDIT_EMAIL_TEMPLATES");
  const canLegal = hasPerm(user, "EDIT_LEGAL_CONTENT");

  const [catalog, setCatalog] = useState({ categories: [] });
  const [stats, setStats] = useState(null);
  const view = category === "emails" ? "emails" : category === "legal" ? "legal" : "content";
  const catFilter = view === "content" ? (category || "") : "";

  const loadMeta = useCallback(async () => {
    const [c, s] = await Promise.all([api.get("/admin/cms/catalog"), api.get("/admin/cms/stats")]);
    setCatalog(c.data); setStats(s.data);
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta]);

  return (
    <div className="space-y-6" data-testid="admin-content-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-white">{t("admin.cms.title", "Contenu du site")}</h1>
        <p className="text-sm text-slate-400">{t("admin.cms.subtitle", "Modifiez les textes de la plateforme. Les changements sont visibles immédiatement (fallback automatique vers le texte par défaut).")}</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <StatCard testid="cms-stat-total" label={t("admin.cms.stat_total", "Textes (total)")} value={stats.content_total} />
          <StatCard testid="cms-stat-modified" label={t("admin.cms.modified", "Modifiés")} value={stats.content_modified} />
          <StatCard testid="cms-stat-never" label={t("admin.cms.stat_never", "Jamais modifiés")} value={stats.content_never_modified} />
          <StatCard testid="cms-stat-emails" label={t("admin.cms.emails", "Emails")} value={`${stats.emails_modified}/${stats.emails_total}`} />
          <StatCard testid="cms-stat-legal" label={t("admin.cms.legal_pages", "Pages légales")} value={`${stats.legal_modified}/${stats.legal_total}`} />
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-slate-800 pb-3">
        <TabBtn active={view === "content"} onClick={() => nav(base)} testid="cms-tab-content">{t("admin.cms.texts", "Textes")}</TabBtn>
        <TabBtn active={view === "emails"} onClick={() => nav(`${base}/emails`)} testid="cms-tab-emails">{t("admin.cms.emails", "Emails")}</TabBtn>
        <TabBtn active={view === "legal"} onClick={() => nav(`${base}/legal`)} testid="cms-tab-legal">{t("admin.cms.legal_pages", "Pages légales")}</TabBtn>
      </div>

      {view === "content" && (
        <ContentTab catFilter={catFilter} categories={catalog.categories} canEdit={canEdit} canPublish={canPublish}
          onCat={(k) => nav(k ? `${base}/${k}` : base)} reloadStats={loadMeta} />
      )}
      {view === "emails" && <EmailsTab canEmail={canEmail} reloadStats={loadMeta} />}
      {view === "legal" && <LegalTab canLegal={canLegal} canPublish={canPublish} reloadStats={loadMeta} />}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-full px-4 py-1.5 text-sm ${active ? "bg-white text-slate-900" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{children}</button>;
}

/* ------------------------------ Content tab ------------------------------ */
function ContentTab({ catFilter, categories, canEdit, canPublish, onCat, reloadStats }) {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState(false);
  const [histKey, setHistKey] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState("");

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (catFilter) params.set("category", catFilter);
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/cms/content?${params.toString()}`);
    setItems(data.items);
  }, [catFilter, q]);
  useEffect(() => { load(); }, [load]);

  const startEdit = (it) => { setEditing(it.key); setDraft(it.current_value); setPreview(false); };
  const save = async (it) => {
    try {
      await api.put(`/admin/cms/content/${it.key}`, { value: draft });
      toast.success(t("admin.cms.text_saved", "Texte enregistré"));
      setEditing(null); await load(); reloadStats();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.save_failed", "Échec de l'enregistrement")); }
  };
  const exportFile = async (fmt) => {
    const res = await api.get(`/admin/cms/export?format=${fmt}`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `cms_content.${fmt}`; a.click(); URL.revokeObjectURL(url);
  };
  const doImport = async () => {
    let parsed; try { parsed = JSON.parse(importText); } catch { toast.error(t("admin.cms.json_invalid", "JSON invalide")); return; }
    if (!Array.isArray(parsed)) { toast.error(t("admin.cms.json_array", "Le JSON doit être un tableau d'objets {key, value}")); return; }
    try {
      const { data } = await api.post("/admin/cms/import", { items: parsed });
      toast.success(t("admin.cms.import_result", "{u} mis à jour · {i} invalides · {s} système ignorés").replace("{u}", data.updated).replace("{i}", data.invalid_keys.length).replace("{s}", data.skipped_system.length));
      setShowImport(false); setImportText(""); await load(); reloadStats();
    } catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.import_failed", "Échec de l'import")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Chip active={!catFilter} onClick={() => onCat("")} testid="cms-cat-all">{t("admin.cms.all", "Tous")}</Chip>
        {categories.filter((c) => !["emails", "legal"].includes(c.key)).map((c) => (
          <Chip key={c.key} active={catFilter === c.key} onClick={() => onCat(c.key)} testid={`cms-cat-${c.key}`}>{c.label}</Chip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="cms-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.cms.search_ph", "Rechercher clé, texte, page, catégorie...")} className="w-full rounded-lg border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm text-slate-100 outline-none focus:border-slate-500" />
        </div>
        <button data-testid="cms-export-json" onClick={() => exportFile("json")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"><Download className="h-4 w-4" /> JSON</button>
        <button data-testid="cms-export-csv" onClick={() => exportFile("csv")} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"><Download className="h-4 w-4" /> CSV</button>
        {canEdit && <button data-testid="cms-import-toggle" onClick={() => setShowImport((s) => !s)} className="rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800">{t("admin.cms.import", "Importer")}</button>}
      </div>

      {showImport && (
        <div className="rounded-lg border border-slate-800 bg-slate-900 p-3" data-testid="cms-import-box">
          <div className="text-xs text-slate-400">{t("admin.cms.import_hint", "Collez un tableau JSON. Les clés inconnues sont ignorées, aucune clé n'est supprimée.")}</div>
          <textarea value={importText} onChange={(e) => setImportText(e.target.value)} rows={4} placeholder='[{"key":"home.hero.badge","value":"..."}]' className={inputDark} data-testid="cms-import-text" />
          <button onClick={doImport} data-testid="cms-import-submit" className="mt-2 rounded-lg bg-white px-3 py-1.5 text-sm font-medium text-slate-900">{t("admin.cms.validate_import", "Valider l'import")}</button>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-800">
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-900 text-xs uppercase text-slate-500">
            <tr>
              <th className="px-4 py-3">{t("admin.cms.th.key", "Clé / Emplacement")}</th>
              <th className="px-4 py-3">{t("admin.cms.th.current", "Texte actuel")}</th>
              <th className="px-4 py-3">{t("admin.cms.th.status", "Statut")}</th>
              <th className="px-4 py-3 text-right">{t("admin.cms.th.actions", "Actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {items.map((it) => (
              <Fragment key={it.key}>
                <tr data-testid={`cms-row-${it.key}`} className="align-top">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 font-mono text-xs text-slate-200">{it.is_system && <Lock className="h-3 w-3 text-amber-400" />}{it.key}</div>
                    <div className="text-[11px] text-slate-500">{it.category} · {it.page} · {it.section}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300"><div className="max-w-md whitespace-pre-line line-clamp-3">{it.current_value}</div>
                    {it.updated_at && <div className="mt-1 text-[11px] text-slate-500">{new Date(it.updated_at).toLocaleString("fr-FR")} · {it.updated_by}</div>}
                  </td>
                  <td className="px-4 py-3"><Badge status={it.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`cms-hist-${it.key}`} onClick={() => setHistKey(it.key)} className="rounded-md border border-slate-700 p-1.5 text-slate-300 hover:bg-slate-800" title={t("admin.cms.history", "Historique")}><History className="h-4 w-4" /></button>
                      <button data-testid={`cms-edit-${it.key}`} disabled={!canEdit || (it.is_system && !canPublish)} onClick={() => startEdit(it)} className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-slate-900 disabled:opacity-40">{t("admin.cms.edit", "Modifier")}</button>
                    </div>
                  </td>
                </tr>
                {editing === it.key && (
                  <tr data-testid={`cms-editor-${it.key}`} className="bg-slate-900/60">
                    <td colSpan={4} className="px-4 py-4">
                      {LONG_TYPES.includes(it.type) || it.is_html
                        ? <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={4} data-testid="cms-editor-input" className={inputDark} />
                        : <input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} data-testid="cms-editor-input" className={inputDark} />}
                      {preview && <div className="mt-2 rounded-lg border border-slate-700 bg-slate-950 p-3 text-sm text-slate-200" data-testid="cms-preview"><div className="text-[11px] uppercase text-slate-500">{t("admin.cms.preview", "Prévisualisation")}</div><div className="mt-1 whitespace-pre-line">{draft}</div></div>}
                      <div className="mt-3 flex gap-2">
                        <button onClick={() => save(it)} data-testid="cms-save" className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white"><Save className="h-4 w-4" /> {t("admin.cms.save", "Enregistrer")}</button>
                        <button onClick={() => setPreview((p) => !p)} data-testid="cms-preview-btn" className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-200">{t("admin.cms.preview_btn", "Prévisualiser")}</button>
                        <button onClick={() => setEditing(null)} data-testid="cms-cancel" className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300"><X className="h-4 w-4" /> {t("admin.cms.cancel", "Annuler")}</button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">{t("admin.cms.no_text", "Aucun texte trouvé.")}</td></tr>}
          </tbody>
        </table>
      </div>

      {histKey && <HistoryModal keyName={histKey} canEdit={canEdit} onClose={() => setHistKey(null)} onRestored={() => { setHistKey(null); load(); reloadStats(); }} />}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return <button data-testid={testid} onClick={onClick} className={`rounded-full px-3 py-1 text-xs ${active ? "bg-accent text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>{children}</button>;
}

function HistoryModal({ keyName, canEdit, onClose, onRestored }) {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/admin/cms/content/${keyName}/history`).then((r) => setItems(r.data)).catch(() => {}); }, [keyName]);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const restore = async (h) => {
    try { await api.post(`/admin/cms/content/${keyName}/restore`, { history_id: h.id }); toast.success(t("admin.cms.version_restored", "Version restaurée")); onRestored(); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.restore_failed", "Échec de la restauration")); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-testid="cms-history-modal" onClick={onClose}>
      <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-display font-semibold text-white">{t("admin.cms.history", "Historique")} — {keyName}</h3><button onClick={onClose} data-testid="cms-history-close" className="text-slate-400 hover:text-white"><X className="h-5 w-5" /></button></div>
        <div className="mt-4 space-y-2">
          {items.length === 0 && <div className="text-sm text-slate-500">{t("admin.cms.no_history", "Aucune modification enregistrée.")}</div>}
          {items.map((h) => (
            <div key={h.id} className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs">
              <div className="text-slate-500">{new Date(h.timestamp).toLocaleString("fr-FR")} · {h.admin_name} · {h.action}</div>
              <div className="mt-1 whitespace-pre-line text-slate-500 line-through">{h.old_value || t("admin.cms.empty", "(vide)")}</div>
              <div className="mt-0.5 whitespace-pre-line text-slate-200">{h.new_value || t("admin.cms.empty", "(vide)")}</div>
              {canEdit && <button onClick={() => restore(h)} data-testid={`cms-restore-${h.id}`} className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800"><RotateCcw className="h-3 w-3" /> {t("admin.cms.restore_this", "Restaurer cette version")}</button>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ Emails tab ------------------------------ */
function EmailsTab({ canEmail, reloadStats }) {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ subject: "", title: "", body: "", footer: "" });
  const load = useCallback(async () => { const { data } = await api.get("/admin/cms/emails"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const open = (e) => { setSel(e); setForm({ subject: e.subject, title: e.title, body: e.body, footer: e.footer }); };
  const save = async () => {
    try { await api.put(`/admin/cms/emails/${sel.id}`, form); toast.success(t("admin.cms.template_saved", "Template enregistré")); setSel(null); await load(); reloadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.save_failed", "Échec de l'enregistrement")); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <div className="space-y-2">
        {items.map((e) => (
          <button key={e.id} data-testid={`cms-email-${e.id}`} onClick={() => open(e)} className={`w-full rounded-lg border p-3 text-left ${sel?.id === e.id ? "border-accent bg-slate-800" : "border-slate-800 bg-slate-900 hover:bg-slate-800"}`}>
            <div className="flex items-center gap-1.5 text-sm text-white">{e.is_system && <Lock className="h-3 w-3 text-amber-400" />}{e.label}</div>
            <div className="text-[11px] text-slate-500">{e.id} · <Badge status={e.status} /></div>
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        {!sel ? <div className="text-sm text-slate-500">{t("admin.cms.select_template", "Sélectionnez un template à gauche.")}</div> : (
          <div className="space-y-3" data-testid="cms-email-form">
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-3 text-xs text-slate-400">
              {t("admin.cms.available_vars", "Variables disponibles :")} {sel.available_vars.map((v) => <code key={v} className="mr-1 rounded bg-slate-800 px-1.5 py-0.5 text-slate-200">{v}</code>)}
              {sel.required_vars.length > 0 && <div className="mt-1 text-amber-300">{t("admin.cms.required_vars", "Obligatoires :")} {sel.required_vars.join(", ")}</div>}
            </div>
            <div><label className="text-sm text-slate-300">{t("admin.cms.subject", "Sujet")}</label><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="cms-email-subject" className={inputDark} /></div>
            <div><label className="text-sm text-slate-300">{t("admin.cms.title_field", "Titre")}</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cms-email-title" className={inputDark} /></div>
            <div><label className="text-sm text-slate-300">{t("admin.cms.content", "Contenu")}</label><textarea rows={5} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="cms-email-body" className={inputDark} /></div>
            <div><label className="text-sm text-slate-300">Footer</label><input value={form.footer} onChange={(e) => setForm({ ...form, footer: e.target.value })} data-testid="cms-email-footer" className={inputDark} /></div>
            <div className="flex gap-2">
              <button disabled={!canEmail} onClick={save} data-testid="cms-email-save" className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">{t("admin.cms.save", "Enregistrer")}</button>
              <button onClick={() => setSel(null)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300">{t("admin.cms.cancel", "Annuler")}</button>
            </div>
            {!canEmail && <div className="text-xs text-amber-300">{t("admin.cms.perm_email", "Permission EDIT_EMAIL_TEMPLATES requise pour enregistrer.")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Legal tab ------------------------------ */
function LegalTab({ canLegal, canPublish, reloadStats }) {
  const { t } = useContent();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = useCallback(async () => { const { data } = await api.get("/admin/cms/legal"); setItems(data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const open = async (slug) => {
    const { data } = await api.get(`/admin/cms/legal/${slug}`);
    setSel(slug); setDetail(data); setForm({ title: data.draft.title, body: data.draft.body });
  };
  const saveDraft = async () => {
    try { await api.put(`/admin/cms/legal/${sel}`, form); toast.success(t("admin.cms.draft_saved", "Brouillon enregistré")); await open(sel); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.failed", "Échec")); }
  };
  const publish = async () => {
    try { const { data } = await api.post(`/admin/cms/legal/${sel}/publish`); toast.success(t("admin.cms.published", "Publié (v{v})").replace("{v}", data.version)); await open(sel); await load(); reloadStats(); }
    catch (e) { toast.error(e.response?.data?.detail || t("admin.cms.publish_failed", "Échec de la publication")); }
  };
  return (
    <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
      <div className="space-y-2">
        {items.map((l) => (
          <button key={l.slug} data-testid={`cms-legal-${l.slug}`} onClick={() => open(l.slug)} className={`w-full rounded-lg border p-3 text-left ${sel === l.slug ? "border-accent bg-slate-800" : "border-slate-800 bg-slate-900 hover:bg-slate-800"}`}>
            <div className="text-sm text-white">{l.title}</div>
            <div className="text-[11px] text-slate-500">v{l.published_version}{l.has_draft ? t("admin.cms.draft_pending", " · brouillon en attente") : ""}</div>
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-slate-800 bg-slate-900 p-4">
        {!sel ? <div className="text-sm text-slate-500">{t("admin.cms.select_page", "Sélectionnez une page à gauche.")}</div> : (
          <div className="space-y-3" data-testid="cms-legal-form">
            <div><label className="text-sm text-slate-300">{t("admin.cms.title_field", "Titre")}</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cms-legal-title" className={inputDark} /></div>
            <div><label className="text-sm text-slate-300">{t("admin.cms.content_html", "Contenu (HTML limité : h2-h4, p, b, i, u, ul, ol, li, a, br, strong, em)")}</label>
              <textarea rows={8} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="cms-legal-body" className={inputDark} /></div>
            <div className="rounded-lg border border-slate-800 bg-slate-950 p-3" data-testid="cms-legal-preview">
              <div className="text-[11px] uppercase text-slate-500">{t("admin.cms.preview", "Prévisualisation")}</div>
              <div className="prose prose-invert mt-1 max-w-none text-sm text-slate-200" dangerouslySetInnerHTML={{ __html: form.body }} />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button disabled={!canLegal} onClick={saveDraft} data-testid="cms-legal-save" className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-200 disabled:opacity-40">{t("admin.cms.save_draft", "Enregistrer le brouillon")}</button>
              <button disabled={!canPublish} onClick={publish} data-testid="cms-legal-publish" className="rounded-lg bg-emerald-500 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40">{t("admin.cms.publish", "Publier")}</button>
              {detail && <span className="text-xs text-slate-500">{t("admin.cms.published_version", "Version publiée :")} v{detail.published.version}{detail.published.published_at ? ` · ${new Date(detail.published.published_at).toLocaleString("fr-FR")}` : ""}</span>}
            </div>
            {!canPublish && <div className="text-xs text-amber-300">{t("admin.cms.perm_publish", "Permission PUBLISH_SITE_CONTENT requise pour publier.")}</div>}
          </div>
        )}
      </div>
    </div>
  );
}
